import { keepPreviousData, useQuery, type UseQueryOptions } from "@tanstack/react-query";
import { createClient } from "@/lib/connect";
import { ConversionService } from "@gen/tracker/v1/conversion_connect";
import {
  Conversion,
  ConversionStatus,
  SearchConversionsRequest,
} from "@gen/tracker/v1/conversion_pb";
import { DateRange, Pagination } from "@gen/tracker/v1/common_pb";
import { csvEscape } from "@/lib/csv";
import type { Tone } from "@fox1ck-org/ui";

/** A status colour: the package's tones without the neutral and the brand ones. */
type StatusTone = Exclude<Tone, "neutral" | "primary">;

const client = createClient(ConversionService);

export const conversionKeys = {
  all: ["conversions"] as const,
  search: (params: SearchConversionsParams) => [...conversionKeys.all, "search", params] as const,
  detail: (clickId: string, dedupKey: string) =>
    [...conversionKeys.all, "detail", clickId, dedupKey] as const,
  timeline: (dedupKey: string) => [...conversionKeys.all, "timeline", dedupKey] as const,
};

export interface SearchConversionsParams {
  /** ISO YYYY-MM-DD bounds, both inclusive, in `timezone` (empty = UTC days). */
  range: { from: string; to: string; timezone?: string };
  campaignId?: string;
  offerId?: string;
  networkId?: string;
  status?: ConversionStatus;
  goalKey?: string;
  /** The ad (creative id) and the lander of the conversion's click. */
  creativeId?: string;
  landerId?: string;
  search?: string;
  limit?: number;
  offset?: number;
}

/** Pure SearchConversionsParams → pb request projection (unit-testable). */
export function buildSearchConversionsRequest(
  params: SearchConversionsParams,
): SearchConversionsRequest {
  return new SearchConversionsRequest({
    pagination: new Pagination({
      limit: params.limit ?? 50,
      offset: params.offset ?? 0,
    }),
    range: new DateRange({ from: params.range.from, to: params.range.to, timezone: params.range.timezone ?? "" }),
    campaignId: params.campaignId ?? "",
    offerId: params.offerId ?? "",
    networkId: params.networkId ?? "",
    status: params.status ?? ConversionStatus.UNSPECIFIED,
    goalKey: params.goalKey ?? "",
    creativeId: params.creativeId ?? "",
    landerId: params.landerId ?? "",
    search: params.search ?? "",
  });
}

export function useSearchConversions(
  params: SearchConversionsParams,
  options?: Partial<UseQueryOptions<SearchConversionsResult>>,
) {
  return useQuery({
    queryKey: conversionKeys.search(params),
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const res = await client.searchConversions(buildSearchConversionsRequest(params));
      const statusCounts = new Map<ConversionStatus, number>();
      for (const c of res.statusCounts) statusCounts.set(c.status, Number(c.count));
      return {
        items: res.items,
        total: res.pagination?.total ?? res.items.length,
        statusCounts,
        revenueUsd: res.revenueUsd,
      };
    },
    ...options,
  });
}

/**
 * Every conversion under `params`, page after page of 500 up to `max` — the
 * card's CSV. The server's own rows, not the page on screen.
 */
export async function fetchAllConversions(params: Omit<SearchConversionsParams, "limit" | "offset">, max = 10_000): Promise<Conversion[]> {
  const out: Conversion[] = [];
  for (let offset = 0; offset < max; offset += 500) {
    const res = await client.searchConversions(buildSearchConversionsRequest({ ...params, limit: 500, offset }));
    out.push(...res.items);
    if (res.items.length < 500) break;
  }
  return out;
}

export interface SearchConversionsResult {
  items: Conversion[];
  total: number;
  /** Every status under the same filters without the status. */
  statusCounts: Map<ConversionStatus, number>;
  /** What the rows under every filter add to revenue, USD (decimal string, "" = unknown). */
  revenueUsd: string;
}

export function useGetConversion(
  clickId: string | undefined,
  dedupKey: string | undefined,
) {
  return useQuery({
    queryKey: conversionKeys.detail(clickId ?? "", dedupKey ?? ""),
    enabled: !!clickId && !!dedupKey,
    queryFn: async () => {
      if (!clickId || !dedupKey) return undefined;
      const res = await client.getConversion({ clickId, dedupKey });
      return res.conversion;
    },
  });
}

/**
 * useConversionTimeline loads the full append-only event history behind one
 * dedup key (HOLD → APPROVED → REFUNDED …). Enabled lazily by the detail
 * drawer so list rendering never fans out timeline RPCs.
 */
export function useConversionTimeline(dedupKey: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: conversionKeys.timeline(dedupKey ?? ""),
    enabled: enabled && !!dedupKey,
    queryFn: async (): Promise<Conversion[]> => {
      if (!dedupKey) return [];
      const res = await client.getConversionTimeline({ dedupKey });
      return res.events;
    },
  });
}

/**
 * The STABLE name of a status — what the CSV column says.
 *
 * Deliberately not the on-screen word: the export is read by a spreadsheet, a
 * reconciliation script and whoever compares this file against Keitaro, and
 * none of them should start seeing a different value because the interface
 * changed language. `conversionStatusText` is the one that speaks to a person.
 */
export function conversionStatusLabel(s: ConversionStatus): string {
  switch (s) {
    case ConversionStatus.HOLD:
      return "Hold";
    case ConversionStatus.APPROVED:
      return "Approved";
    case ConversionStatus.REJECTED:
      return "Rejected";
    case ConversionStatus.REFUNDED:
      return "Refunded";
    case ConversionStatus.TRASH:
      return "Trash";
    default:
      return "—";
  }
}

/**
 * What the pill and the filter say — the words of доска Metrics («Апрувы ·
 * Холд · Отказ · Треш»), so a status reads the same in the journal, the
 * report's columns and the drawer.
 */
export function conversionStatusText(s: ConversionStatus): string {
  switch (s) {
    case ConversionStatus.HOLD:
      return "Холд";
    case ConversionStatus.APPROVED:
      return "Апрув";
    case ConversionStatus.REJECTED:
      return "Отказ";
    case ConversionStatus.REFUNDED:
      return "Возврат";
    // TRASH is not a rejection: REJECTED is a real order the partner refused —
    // a number worth arguing about — while TRASH was never an order at all.
    case ConversionStatus.TRASH:
      return "Треш";
    default:
      return "—";
  }
}

/** The pill's tone: апрув ok, отказ red, треш and возврат amber, холд neutral. */
export function conversionStatusTone(s: ConversionStatus): StatusTone | undefined {
  switch (s) {
    case ConversionStatus.APPROVED:
      return "ok";
    case ConversionStatus.REJECTED:
      return "danger";
    case ConversionStatus.TRASH:
    case ConversionStatus.REFUNDED:
      return "warn";
    default:
      return undefined;
  }
}

/**
 * parseRawPayload — the inbound postback `raw_payload` is the original
 * querystring the network sent. Returns ordered [key, value] pairs, or null
 * when the payload is empty / not querystring-shaped (caller falls back to
 * rendering the raw string).
 */
export function parseRawPayload(raw: string): [string, string][] | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const qs = trimmed.startsWith("?") ? trimmed.slice(1) : trimmed;
  // JSON or free-text blobs are not querystrings — bail to the raw fallback.
  if (!qs.includes("=") || qs.startsWith("{") || qs.startsWith("[")) return null;
  try {
    const out: [string, string][] = [];
    for (const [k, v] of new URLSearchParams(qs).entries()) {
      out.push([k, v]);
    }
    return out.length > 0 ? out : null;
  } catch {
    return null;
  }
}

// conversionRowsToCsv mirrors the table grid columns. Pure helper.
export function conversionRowsToCsv(rows: Conversion[]): string {
  const header = [
    "ts",
    "status",
    "goal_key",
    "payout",
    "payout_currency",
    "txid",
    "click_id",
    "campaign_id",
    "offer_id",
    "network_id",
    "country",
    "reject_reason",
  ];
  const lines: string[] = [header.map(csvEscape).join(",")];
  for (const c of rows) {
    lines.push(
      [
        c.ts,
        conversionStatusLabel(c.status),
        c.goalKey,
        c.payout?.amount ?? "0",
        c.payout?.currency ?? "",
        c.txid,
        c.clickId,
        c.campaignId,
        c.offerId,
        c.networkId,
        c.country,
        c.rejectReason,
      ]
        .map(csvEscape)
        .join(","),
    );
  }
  return lines.join("\r\n");
}
